import { useState, useContext } from "react";
import "../account.css";
import "./accountSecurity.css";
import { CombinedContext } from "../../../context/combinedContext";
import { clearTokens } from "../../../auth/tokenManager";
import translations from "../../../translations/translations";
import usersRequest from "../../../request/usersRequest";
import companyBasedRequest from "../../../request/companyBasedRequest";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import { useNavigate } from "react-router-dom";
import PhoneInput from "../../common/phoneInput/phoneInput";
import useModalDismiss from "../../common/useModalDismiss";
import { AtSign, CheckCircle2, KeyRound, Phone, ShieldAlert, Trash2, TriangleAlert } from "lucide-react";

const AccountSecurity = () => {
	const { language, userData, setUserData } = useContext(CombinedContext);

	const userEmail = userData?.user?.email || userData?.email || "";
	const userPhone = userData?.user?.phone || userData?.user?.phoneNumber || "";
	const accessToken = userData?.cognitoTokens?.AccessToken || userData?.accessToken;

	const [success, setSuccess] = useState(false);
	const [toast, setToast] = useState("");
	const [email, setEmail] = useState(userEmail);
	const [loading, setLoading] = useState(false);
	const [phone, setPhone] = useState(userPhone);
	const [phoneSaving, setPhoneSaving] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [banner, setBanner] = useState({ text: "", type: "info", exiting: false });
	const [shopConfirmText, setShopConfirmText] = useState("");
	const [requestingShopDeletion, setRequestingShopDeletion] = useState(false);
	const [shopDeleteRequested, setShopDeleteRequested] = useState(false);

	/* no Escape mid-delete — the account is already on its way out */
	useModalDismiss(() => setShowDeleteConfirm(false), { enabled: showDeleteConfirm && !deleting });

	const shopName = userData?.shop?.name || "";
	const shopNameMatches = shopConfirmText.trim().toLowerCase() === shopName.trim().toLowerCase() && shopName.length > 0;

	const t = translations[language].account.security || {};
	const fp = translations[language].account.forgotPassword;
	const es = language === "es";

	const navigate = useNavigate();

	async function handleResetPassword(e) {
		e.preventDefault();
		if (!email) return;

		try {
			setLoading(true);
			setToast("");
			await usersRequest.forgotPassword(email);
			setSuccess(true);
		} catch (err) {
			console.error(err);
			setToast(fp.toast.error);
		} finally {
			setLoading(false);
		}
	}

	async function handleSavePhone(e) {
		e.preventDefault();
		if (!phone || phone === userPhone) return;

		try {
			setPhoneSaving(true);
			const res = await usersRequest.updatePhone(phone, accessToken);
			setUserData((prev) => ({ ...prev, user: { ...prev.user, phone: res.user?.phone ?? phone } }));
			setBanner({ text: t.phoneSuccess || "Phone number updated", type: "success", exiting: false });
		} catch (err) {
			console.error(err);
			setBanner({ text: err.message || t.phoneError || "Could not update phone number", type: "error", exiting: false });
		} finally {
			setPhoneSaving(false);
		}
	}

	async function handleDeleteAccount() {
		try {
			setDeleting(true);
			await usersRequest.removeSelf(accessToken);
			clearTokens();
			setUserData(null);
			navigate("/signin");
		} catch (err) {
			console.error(err);
			setShowDeleteConfirm(false);
			setToast(es ? "Error al eliminar la cuenta. Inténtalo de nuevo." : "Failed to delete account. Please try again.");
		} finally {
			setDeleting(false);
		}
	}

	async function handleRequestShopDeletion(e) {
		e.preventDefault();
		if (!shopNameMatches || !userData?.shop?.shopId) return;

		try {
			setRequestingShopDeletion(true);
			await companyBasedRequest.requestShopDeletion(userData.shop.shopId, shopConfirmText, accessToken);
			setShopDeleteRequested(true);
		} catch (err) {
			console.error(err);
			setToast(
				err.message ||
					(es
						? "No se pudo enviar la solicitud de eliminación. Inténtalo de nuevo."
						: "Could not send the deletion request. Please try again.")
			);
		} finally {
			setRequestingShopDeletion(false);
		}
	}

	const isCustomer = userData?.user?.role === "customer";
	const isOwner = userData?.user?.role === "owner";

	return (
		<div className="ac-page aps">
			<header className="ac-head">
				{/* <span className="ac-eyebrow">{translations[language].account.menu?.security || "Security"}</span> */}
				<h1 className="ac-title">{t.title || "Security"}</h1>
				{t.subtitle && <p className="ac-sub">{t.subtitle}</p>}
			</header>

			{/* ── EMAIL ── */}
			<section className="ac-card">
				<div className="ac-card-head">
					<span className="ac-icon">
						<AtSign size={16} />
					</span>
					<h3>{t.emailTitle || "Email"}</h3>
				</div>

				<div className="ac-card-body">
					<div className="aps-readonly">{userEmail || "—"}</div>
					{t.emailHint && <p className="aps-hint">{t.emailHint}</p>}
				</div>
			</section>

			{/* ── PHONE ── */}
			<section className="ac-card">
				<div className="ac-card-head">
					<span className="ac-icon">
						<Phone size={16} />
					</span>
					<h3>{t.phoneTitle || "Phone Number"}</h3>
				</div>

				<form className="ac-card-body" onSubmit={handleSavePhone}>
					{t.phoneHint && <p className="aps-hint">{t.phoneHint}</p>}

					<div className="ac-inline aps-phone-row">
						<PhoneInput businessPhone={phone} setBusinessPhone={setPhone} />
						<button type="submit" className="ac-btn" disabled={phoneSaving || !phone || phone === userPhone}>
							{phoneSaving ? t.phoneSaving || "Saving..." : t.phoneSave || "Save"}
						</button>
					</div>
				</form>
			</section>

			{/* ── RESET PASSWORD ── */}
			<section className="ac-card">
				<div className="ac-card-head">
					<span className="ac-icon">
						<KeyRound size={16} />
					</span>
					<h3>{t.passwordTitle || "Reset Password"}</h3>
				</div>

				<div className="ac-card-body">
					{success ? (
						<div className="ac-note is-ok">
							<CheckCircle2 size={14} />
							<span>{fp.success}</span>
						</div>
					) : (
						<form className="aps-form" onSubmit={handleResetPassword}>
							<p className="aps-hint">{fp.subtitle}</p>

							<div className="ac-inline">
								<input type="email" className="ac-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
								<button type="submit" className="ac-btn ac-btn--ghost" disabled={loading}>
									{loading ? fp.sending : fp.send}
								</button>
							</div>
						</form>
					)}
				</div>
			</section>

			{/* ── DANGER ZONE ── */}
			{(isCustomer || isOwner) && (
				<>
					{/* <p className="ac-label aps-danger-label">
						<TriangleAlert size={13} />
						{t.dangerZone || (es ? "Zona de riesgo" : "Danger zone")}
					</p> */}

					{/* DELETE ACCOUNT (customer) */}
					{isCustomer && (
						<section className="ac-card is-danger">
							<div className="ac-card-head">
								<span className="ac-icon is-danger">
									<Trash2 size={16} />
								</span>
								<h3>{es ? "Eliminar cuenta" : "Delete Account"}</h3>
							</div>

							<div className="ac-card-body">
								<p className="aps-hint">
									{es ? "Esta acción es permanente y no se puede deshacer." : "This action is permanent and cannot be undone."}
								</p>
								<button className="ac-btn ac-btn--danger-ghost aps-danger-btn" onClick={() => setShowDeleteConfirm(true)}>
									{es ? "Eliminar cuenta" : "Delete Account"}
								</button>
							</div>
						</section>
					)}

					{/* DELETE MY SHOP (owner) */}
					{isOwner && (
						<section className="ac-card is-danger">
							<div className="ac-card-head">
								<span className="ac-icon is-danger">
									<ShieldAlert size={16} />
								</span>
								<h3>{es ? "Eliminar mi negocio" : "Delete My Shop"}</h3>
							</div>

							<div className="ac-card-body">
								<div className="ac-note is-danger">
									<TriangleAlert size={14} />
									<span>
										{es
											? "Esta acción es permanente. Se eliminará el perfil de tu negocio, las cuentas de todo tu personal, los horarios y las reservas futuras (se notificará a tus clientes). Tus reseñas e historial dejarán de ser visibles. Te recomendamos guardar manualmente cualquier información que quieras conservar antes de continuar."
											: "This action is permanent. It will delete your shop profile, all staff accounts, schedules, and future bookings (your customers will be notified). Your reviews and history will no longer be visible. We recommend manually saving anything you want to keep before continuing."}
									</span>
								</div>

								{shopDeleteRequested ? (
									<div className="ac-note is-ok">
										<CheckCircle2 size={14} />
										<span>
											{es
												? "Revisa tu correo para confirmar la eliminación. El enlace expira en 1 hora."
												: "Check your email to confirm deletion. The link expires in 1 hour."}
										</span>
									</div>
								) : (
									<form className="aps-form" onSubmit={handleRequestShopDeletion}>
										<p className="aps-hint">
											{es ? "Escribe " : "Type "}
											<strong>{shopName}</strong>
											{es ? " para confirmar:" : " to confirm:"}
										</p>

										<div className="ac-inline">
											<input
												type="text"
												className="ac-input"
												value={shopConfirmText}
												onChange={(e) => setShopConfirmText(e.target.value)}
												placeholder={shopName}
											/>
											<button
												type="submit"
												className="ac-btn ac-btn--danger"
												disabled={requestingShopDeletion || !shopNameMatches}
											>
												{requestingShopDeletion
													? es
														? "Enviando..."
														: "Sending..."
													: es
														? "Enviar correo de confirmación"
														: "Send confirmation email"}
											</button>
										</div>
									</form>
								)}
							</div>
						</section>
					)}
				</>
			)}

			{/* ── CONFIRM MODAL ── */}
			{showDeleteConfirm && (
				<div className="aps-overlay">
					<div className="aps-modal">
						<span className="ac-icon is-danger aps-modal-icon">
							<TriangleAlert size={20} />
						</span>

						<h3>{es ? "¿Eliminar tu cuenta?" : "Delete your account?"}</h3>
						<p>
							{es
								? "Todos tus datos serán eliminados de forma permanente. Esta acción no se puede deshacer."
								: "All your data will be permanently deleted. This cannot be undone."}
						</p>

						<div className="aps-modal-actions">
							<button className="ac-btn ac-btn--ghost" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
								{es ? "Cancelar" : "Go back"}
							</button>
							<button className="ac-btn ac-btn--danger" onClick={handleDeleteAccount} disabled={deleting}>
								{deleting ? (es ? "Eliminando..." : "Deleting...") : es ? "Sí, eliminar" : "Yes, delete"}
							</button>
						</div>
					</div>
				</div>
			)}

			{banner.text && <BannerMessage text={banner.text} type={banner.type} exiting={banner.exiting} />}
			{toast && <BannerMessage text={toast} />}
		</div>
	);
};

export default AccountSecurity;
